/**
 * Painel do fabricante — Trilha de acesso.
 *
 * Tela do DONO DO PRODUTO. Existe porque contra quem tem credencial legítima
 * não há prevenção: há reduzir alcance, detectar e atribuir. Aqui se vê QUEM
 * acessou dado sensível, QUANDO, DE ONDE e QUANTO — nunca o conteúdo
 * consultado, para a trilha não virar um segundo lugar onde o dado se acumula.
 *
 * Só aparece para `fabricante_admin` em instância com AURA_FABRICANTE=1; para o
 * administrador da operadora as rotas respondem 404.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Activity, AlertTriangle, Download, Monitor, Clock } from 'lucide-react';
import fabricanteAPI from '../../services/fabricanteAPI';

const T = {
  ink: '#2c3e50', inkSoft: '#7f8c8d', line: '#e8e8e8', card: '#fff', bg: '#f5f7fa',
  risco: '#e74c3c', riscoSoft: '#fdecea', ambar: '#f39c12', ambarSoft: '#fef5e7',
  teal: 'var(--brand, #20a490)', azul: '#3498db',
};
const MONO = "'SF Mono','JetBrains Mono',Consolas,monospace";
const fmtN = (v) => Number(v || 0).toLocaleString('pt-BR');
const quando = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR') : '—');

const Card = ({ children, style }) => (
  <div style={{ background: T.card, border: `1px solid ${T.line}`, borderRadius: 12, padding: 18, ...style }}>
    {children}
  </div>
);
const TH = { padding: '9px 8px', fontWeight: 600, textAlign: 'left', color: T.inkSoft, position: 'sticky', top: 0, background: '#fff' };
const TD = { padding: '8px', color: T.ink, verticalAlign: 'top' };

export default function TrilhaAcesso() {
  const [horas, setHoras] = useState(24);
  const [resumo, setResumo] = useState(null);
  const [eventos, setEventos] = useState([]);
  const [sessoes, setSessoes] = useState([]);
  const [soExtracao, setSoExtracao] = useState(false);
  const [usuarioId, setUsuarioId] = useState(null);
  const [erro, setErro] = useState(null);

  const carregar = useCallback(async () => {
    try {
      const [r, e, s] = await Promise.all([
        fabricanteAPI.resumoTrilha(horas),
        fabricanteAPI.listarTrilha({ horas, limite: 200, so_extracao: soExtracao, ...(usuarioId ? { usuario_id: usuarioId } : {}) }),
        fabricanteAPI.sessoes(7),
      ]);
      setResumo(r); setEventos(e); setSessoes(s); setErro(null);
    } catch (ex) {
      setErro(ex?.response?.status === 404
        ? 'Painel do fabricante não habilitado nesta instância.'
        : 'Não consegui carregar a trilha.');
    }
  }, [horas, soExtracao, usuarioId]);

  useEffect(() => { carregar(); }, [carregar]);

  return (
    <div style={{ background: T.bg, minHeight: '100%', padding: 24 }}>
      <h1 style={{ fontSize: 22, color: T.ink, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
        <Activity size={22} color={T.teal} /> Trilha de acesso
      </h1>
      <p style={{ fontSize: 13.5, color: T.inkSoft, margin: '4px 0 18px', maxWidth: 720 }}>
        Quem abriu dado de beneficiário, financeiro ou bancário: quando, de onde e quanto.
        Volume alto sozinho não acusa nada; o que acusa é volume fora do padrão da pessoa
        e do horário.
      </p>

      {erro && (
        <Card style={{ background: T.ambarSoft, border: `1px solid ${T.ambar}`, marginBottom: 16 }}>
          <span style={{ color: T.ambar, fontWeight: 600 }}>{erro}</span>
        </Card>
      )}

      <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={horas} onChange={(e) => setHoras(Number(e.target.value))}
          style={{ padding: '8px 10px', border: `1px solid ${T.line}`, borderRadius: 8 }}>
          <option value={24}>Últimas 24 horas</option>
          <option value={72}>Últimos 3 dias</option>
          <option value={168}>Últimos 7 dias</option>
          <option value={720}>Últimos 30 dias</option>
        </select>
        <label style={{ fontSize: 13, color: T.ink, display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={soExtracao} onChange={(e) => setSoExtracao(e.target.checked)} />
          só extração (dado saindo do sistema)
        </label>
        {usuarioId && (
          <button onClick={() => setUsuarioId(null)}
            style={{ border: `1px solid ${T.line}`, background: '#fff', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 13 }}>
            limpar filtro de usuário
          </button>
        )}
        <div style={{ flex: 1 }} />
        {resumo && <span style={{ fontSize: 13, color: T.inkSoft }}>{fmtN(resumo.total_eventos)} eventos na janela</span>}
      </div>

      {/* Por usuário — é aqui que o anormal aparece */}
      <Card style={{ marginBottom: 18, padding: 0, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr>
            <th style={TH}>Pessoa</th><th style={TH}>Perfil</th>
            <th style={{ ...TH, textAlign: 'right' }}>Acessos</th>
            <th style={{ ...TH, textAlign: 'right' }}>Extrações</th>
            <th style={{ ...TH, textAlign: 'right' }}>Fora do horário</th>
            <th style={{ ...TH, textAlign: 'right' }}>IPs</th>
            <th style={TH}>Último</th>
          </tr></thead>
          <tbody>
            {resumo?.por_usuario?.map((u) => {
              const suspeito = u.extracoes > 0 || u.fora_do_horario > 0 || u.ips > 2;
              return (
                <tr key={u.usuario_id} onClick={() => setUsuarioId(u.usuario_id)}
                  style={{ borderTop: `1px solid ${T.line}`, cursor: 'pointer', background: suspeito ? T.riscoSoft : 'transparent' }}>
                  <td style={TD}>
                    <div style={{ fontWeight: 600 }}>{u.nome || '—'}</div>
                    <div style={{ fontSize: 11.5, color: T.inkSoft, fontFamily: MONO }}>{u.email}</div>
                  </td>
                  <td style={TD}>{u.perfil?.replace('PerfilUsuarioEnum.', '')}</td>
                  <td style={{ ...TD, textAlign: 'right', fontFamily: MONO, fontWeight: 700 }}>{fmtN(u.eventos)}</td>
                  <td style={{ ...TD, textAlign: 'right', fontFamily: MONO, color: u.extracoes ? T.risco : T.inkSoft }}>{fmtN(u.extracoes)}</td>
                  <td style={{ ...TD, textAlign: 'right', fontFamily: MONO, color: u.fora_do_horario ? T.ambar : T.inkSoft }}>{fmtN(u.fora_do_horario)}</td>
                  <td style={{ ...TD, textAlign: 'right', fontFamily: MONO }}>{u.ips}</td>
                  <td style={{ ...TD, color: T.inkSoft, fontSize: 12 }}>{quando(u.ultimo)}</td>
                </tr>
              );
            })}
            {!resumo?.por_usuario?.length && (
              <tr><td style={{ ...TD, padding: 20, color: T.inkSoft }} colSpan={7}>
                Nenhum acesso registrado nesta janela.
              </td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {/* Eventos */}
      <h3 style={{ fontSize: 15, color: T.ink, margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <Clock size={16} color={T.inkSoft} /> Eventos
      </h3>
      <Card style={{ marginBottom: 18, padding: 0, overflow: 'auto', maxHeight: 420 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead><tr>
            <th style={TH}>Quando</th><th style={TH}>Pessoa</th><th style={TH}>O que abriu</th>
            <th style={TH}>Alvo</th><th style={TH}>IP</th>
          </tr></thead>
          <tbody>
            {eventos.map((e) => (
              <tr key={e.id} style={{ borderTop: `1px solid ${T.line}`, background: e.extracao ? T.riscoSoft : 'transparent' }}>
                <td style={{ ...TD, whiteSpace: 'nowrap', color: T.inkSoft }}>{quando(e.quando)}</td>
                <td style={TD}>{e.nome || e.email}</td>
                <td style={{ ...TD, fontFamily: MONO }}>
                  {e.acao}
                  {e.extracao && (
                    <span style={{ marginLeft: 6, color: T.risco, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                      <Download size={11} /> extração
                    </span>
                  )}
                </td>
                <td style={{ ...TD, fontFamily: MONO, color: T.inkSoft, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {(e.detalhes || '').replace('[EXTRACAO]', '').trim() || '—'}
                </td>
                <td style={{ ...TD, fontFamily: MONO, color: T.inkSoft }}>{e.ip || '—'}</td>
              </tr>
            ))}
            {!eventos.length && (
              <tr><td style={{ ...TD, padding: 20, color: T.inkSoft }} colSpan={5}>Nada nesta seleção.</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {/* Sessões: já era gravado e ninguém olhava */}
      <h3 style={{ fontSize: 15, color: T.ink, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <Monitor size={16} color={T.inkSoft} /> De onde entraram (7 dias)
      </h3>
      <p style={{ fontSize: 12.5, color: T.inkSoft, margin: '0 0 8px' }}>
        IP e navegador já eram gravados desde sempre: acesso de madrugada ou de um IP inédito
        é sinal antes mesmo de haver volume.
      </p>
      <Card style={{ padding: 0, overflow: 'auto', maxHeight: 320 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead><tr>
            <th style={TH}>Login</th><th style={TH}>Pessoa</th><th style={TH}>IP</th>
            <th style={TH}>Navegador</th><th style={TH}>Sessão</th>
          </tr></thead>
          <tbody>
            {sessoes.map((s, i) => (
              <tr key={i} style={{ borderTop: `1px solid ${T.line}` }}>
                <td style={{ ...TD, whiteSpace: 'nowrap', color: T.inkSoft }}>{quando(s.login)}</td>
                <td style={TD}>{s.nome || s.email}</td>
                <td style={{ ...TD, fontFamily: MONO }}>{s.ip || '—'}</td>
                <td style={{ ...TD, color: T.inkSoft, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.navegador}</td>
                <td style={TD}>
                  {s.ativa
                    ? <span style={{ color: T.azul, fontWeight: 600 }}>ativa</span>
                    : <span style={{ color: T.inkSoft }}>encerrada</span>}
                </td>
              </tr>
            ))}
            {!sessoes.length && (
              <tr><td style={{ ...TD, padding: 20, color: T.inkSoft }} colSpan={5}>Nenhuma sessão no período.</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      <p style={{ fontSize: 12, color: T.inkSoft, marginTop: 14, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
        <AlertTriangle size={13} style={{ marginTop: 2, flexShrink: 0 }} />
        A trilha registra o acesso, não o conteúdo: dá para saber que uma ficha foi aberta, não o que ela mostrava. O registro começa na versão que trouxe esta tela. Acesso anterior a ela não existe em lugar nenhum.
      </p>
    </div>
  );
}
